import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Link } from 'react-router-dom';
import { GoogleMap } from '@react-google-maps/api';
import { Card, CardHeader, CardBody } from '../components/Card';
import { Alert } from '../components/Alert';
import { parseMonthDay, daysUntil } from '../utils/dateEvents';
import { mapAPI } from '../api/mapAPI';
import { memberAPI } from '../api/memberAPI';
import { serviceAPI } from '../api/serviceAPI';
import { announcementAPI } from '../api/announcementAPI';
import { useGoogleMapsLoader } from '../hooks/useGoogleMapsLoader';
import { Avatar } from '../components/Avatar';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

const FALLBACK_COLORS = ['#34A853', '#4285F4', '#FBBC05', '#EA4335', '#8E24AA', '#00ACC1'];
const LEADER_COLOR = '#FFD700';

const dashboardMapStyle = { width: '100%', height: '100%' };
const defaultCenter = { lat: 39.8283, lng: -98.5795 };
const mapOptions = {
    styles: [
        { elementType: 'geometry', stylers: [{ saturation: -100 }] },
        { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
        { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
        { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#bdbdbd' }] },
        { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
        { featureType: 'administrative.locality', elementType: 'labels', stylers: [{ visibility: 'off' }] },
        { featureType: 'administrative.neighborhood', elementType: 'labels', stylers: [{ visibility: 'off' }] },
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#e0e0e0' }] },
        { featureType: 'road.highway', elementType: 'labels', stylers: [{ visibility: 'simplified' }] },
        { featureType: 'road.arterial', stylers: [{ visibility: 'off' }] },
        { featureType: 'road.local', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
        { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#e0e0e0' }] },
        { featureType: 'water', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    ],
};

function toDateStr(d) {
    return d.toISOString().split('T')[0];
}

function makeSvgUrl(color, shape) {
    if (shape === 'star') {
        return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
            `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28"><polygon points="14,2 17.5,10.5 27,10.5 19.5,16 22,25 14,20 6,25 8.5,16 1,10.5 10.5,10.5" fill="${color}" stroke="#ffffff" stroke-width="1.5"/></svg>`
        );
    }
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"><circle cx="8" cy="8" r="7" fill="${color}" stroke="#ffffff" stroke-width="1.5"/></svg>`
    );
}

export function Dashboard() {
    const { user, isAdmin } = useAuth();
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const { isLoaded, loadError } = useGoogleMapsLoader();
    const [mapData, setMapData] = useState({ members: [], leaders: [], regions: [] });
    const [mapLoading, setMapLoading] = useState(true);
    const [mapInstance, setMapInstance] = useState(null);
    const markersRef = useRef([]);
    const polygonsRef = useRef([]);

    const [members, setMembers] = useState([]);
    const [membersLoading, setMembersLoading] = useState(true);
    const [avgAttendance, setAvgAttendance] = useState(null);
    const [serviceCount, setServiceCount] = useState(null);
    const [prayerCount, setPrayerCount] = useState(null);
    const [announcements, setAnnouncements] = useState([]);
    const [dismissedAnnouncements, setDismissedAnnouncements] = useState(false);

    useEffect(() => {
        if (!user) return;
        mapAPI.getMapData().then((res) => setMapData(res.data)).catch(() => {}).finally(() => setMapLoading(false));
    }, [user]);

    useEffect(() => {
        if (!user) return;
        memberAPI.getMembers({ per_page: 1000 }).then((res) => {
            setMembers((res.data.data || {}).items || []);
        }).catch(() => {}).finally(() => setMembersLoading(false));
    }, [user]);

    useEffect(() => {
        if (!user) return;
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 30);
        serviceAPI.getReport({ start_date: toDateStr(start), end_date: toDateStr(end) }).then((res) => {
            const data = res.data.data || res.data;
            if (data.avg_weekly_rate != null) {
                setAvgAttendance(data.avg_weekly_rate.toFixed(1));
            }
            if (data.total_weeks != null) {
                setServiceCount(data.total_weeks);
            }
        }).catch(() => {});
    }, [user]);

    useEffect(() => {
        if (!user) return;
        memberAPI.getAllPrayerRequests().then((res) => {
            const data = res.data.data || [];
            setPrayerCount(data.length);
        }).catch(() => {});
    }, [user]);

    useEffect(() => {
        if (!user) return;
        announcementAPI.getActive().then((res) => {
            setAnnouncements(res.data.data || []);
        }).catch(() => {});
    }, [user]);

    const regionColorMap = useMemo(() => {
        const map = {};
        (mapData.regions || []).forEach((r, idx) => { map[r.id] = r.color || FALLBACK_COLORS[idx % FALLBACK_COLORS.length]; });
        return map;
    }, [mapData.regions]);

    const getColor = useCallback((regionId) => regionColorMap[regionId] || '#9CA3AF', [regionColorMap]);

    const onMapLoad = useCallback((map) => { setMapInstance(map); }, []);

    useEffect(() => {
        if (!mapInstance || !window.google || mapLoading) return;
        const map = mapInstance;
        const gm = window.google.maps;
        markersRef.current.forEach((m) => m.setMap(null));
        markersRef.current = [];
        polygonsRef.current.forEach((p) => p.setMap(null));
        polygonsRef.current = [];

        const bounds = new gm.LatLngBounds();
        let hasPoints = false;
        const groups = {};
        const makeKey = (lat, lng) => `${lat.toFixed(6)},${lng.toFixed(6)}`;

        mapData.members.forEach((m) => {
            if (m.latitude && m.longitude) {
                const key = makeKey(m.latitude, m.longitude);
                if (!groups[key]) groups[key] = { lat: m.latitude, lng: m.longitude, people: [] };
                groups[key].people.push({ isLeader: false, regionId: m.region_id });
            }
        });
        mapData.leaders.forEach((l) => {
            if (l.latitude && l.longitude) {
                const key = makeKey(l.latitude, l.longitude);
                if (!groups[key]) groups[key] = { lat: l.latitude, lng: l.longitude, people: [] };
                groups[key].people.push({ isLeader: true, regionId: l.region_id });
            }
        });

        Object.values(groups).forEach((group) => {
            const hasLeader = group.people.some((p) => p.isLeader);
            const firstPerson = group.people[0];
            const color = hasLeader ? LEADER_COLOR : getColor(firstPerson.regionId);
            const shape = hasLeader ? 'star' : 'circle';
            const size = hasLeader ? 28 : 16;
            const position = { lat: group.lat, lng: group.lng };
            const marker = new gm.Marker({
                position, map,
                icon: { url: makeSvgUrl(color, shape), scaledSize: new gm.Size(size, size), anchor: new gm.Point(size / 2, size / 2) },
                clickable: false,
            });
            markersRef.current.push(marker);
            bounds.extend(position);
            hasPoints = true;
        });

        mapData.regions.forEach((r) => {
            if (!r.boundary) return;
            const coords = typeof r.boundary === 'string' ? JSON.parse(r.boundary) : r.boundary;
            if (!Array.isArray(coords) || coords.length < 3) return;
            const color = getColor(r.id);
            const polygon = new gm.Polygon({ paths: coords, fillColor: color, fillOpacity: 0.15, strokeColor: color, strokeWeight: 2, map });
            polygonsRef.current.push(polygon);
            coords.forEach((c) => { bounds.extend({ lat: c.lat, lng: c.lng }); hasPoints = true; });
        });

        if (hasPoints) map.fitBounds(bounds);
    }, [mapData, getColor, mapInstance, mapLoading]);

    const upcomingEvents = useMemo(() => {
        const list = [];
        const addPerson = (p) => {
            const bd = parseMonthDay(p.birthdate);
            if (bd) list.push({ type: 'birthday', name: p.name, photoUrl: p.photo_url, month: bd.month, day: bd.day });
            const wa = parseMonthDay(p.wedding_anniversary);
            if (wa) list.push({ type: 'anniversary', name: p.name, photoUrl: p.photo_url, month: wa.month, day: wa.day });
        };
        members.forEach(addPerson);
        (mapData.leaders || []).forEach(addPerson);
        return list.map((e) => ({ ...e, daysAway: daysUntil(e.month, e.day) })).sort((a, b) => a.daysAway - b.daysAway).slice(0, 8);
    }, [members, mapData.leaders]);

    const memberCount = members.length;
    const leaderCount = (mapData.leaders || []).length;
    const regionCount = (mapData.regions || []).length;

    if (!user) return null;
    const mapsAvailable = isLoaded && !loadError;

    const StatCard = ({ value, label, sub, to }) => {
        const content = (
            <>
                <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">{value}</div>
                <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1">{label}</div>
                {sub && <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">{sub}</div>}
            </>
        );
        if (to) {
            return (
                <Link to={to} className="card-base p-4 text-center block hover:shadow-card-hover transition-shadow">
                    {content}
                </Link>
            );
        }
        return <div className="card-base p-4 text-center">{content}</div>;
    };

    return (
        <div className="p-4 lg:p-6 max-w-content mx-auto animate-fade-in">
            <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-4">Welcome back, {user.name}</h1>
            {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

            {/* Announcements Banner */}
            {announcements.length > 0 && !dismissedAnnouncements && (
                <div className="card-base border-l-4 border-l-amber-400 mb-4">
                    <div className="p-4 flex items-start gap-3">
                        <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 17H2a3 3 0 0 0 3-3V9a7 7 0 0 1 14 0v5a3 3 0 0 0 3 3zm-8.27 4a2 2 0 0 1-3.46 0" />
                        </svg>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{announcements[0].title}</h3>
                            {announcements[0].body && (
                                <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5 whitespace-pre-wrap">{announcements[0].body}</p>
                            )}
                            {announcements.length > 1 && (
                                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">and {announcements.length - 1} more announcement{announcements.length > 2 ? 's' : ''}</p>
                            )}
                        </div>
                        <button
                            onClick={() => setDismissedAnnouncements(true)}
                            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 flex-shrink-0"
                        >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* Stat Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
                <StatCard value={memberCount} label="Members" to="/members" />
                {isAdmin && <StatCard value={leaderCount} label="Leaders" to="/users" />}
                {isAdmin && <StatCard value={regionCount} label="Regions" to="/regions" />}
                {avgAttendance != null && (
                    <StatCard
                        value={`${avgAttendance}%`}
                        label="Attendance"
                        sub={serviceCount != null ? `${serviceCount} weeks` : 'Last 30 days'}
                        to="/attendance/reports"
                    />
                )}
                {prayerCount != null && <StatCard value={prayerCount} label="Prayer Requests" to="/prayers" />}
            </div>

            {/* Map + Events */}
            <div className="grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                    <Card>
                        <CardHeader><h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">Map Overview</h2></CardHeader>
                        <CardBody>
                            {mapLoading || !isLoaded ? (
                                <p className="text-center text-slate-500 py-8">Loading map...</p>
                            ) : !mapsAvailable ? (
                                <p className="text-center text-slate-500 py-8">Map not available</p>
                            ) : (
                                <>
                                    <div className="h-96 rounded-lg overflow-hidden">
                                        <GoogleMap
                                            mapContainerStyle={dashboardMapStyle}
                                            center={defaultCenter}
                                            zoom={6}
                                            options={{ ...mapOptions, disableDefaultUI: true, gestureHandling: 'cooperative' }}
                                            onLoad={onMapLoad}
                                        />
                                    </div>
                                    <div className="text-right mt-3">
                                        <Link to="/map" className="text-sm font-medium text-accent-500 hover:text-accent-600">View Full Map &rarr;</Link>
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>
                </div>

                <div className="lg:col-span-1">
                    <Card>
                        <CardHeader><h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">Upcoming Events</h2></CardHeader>
                        <CardBody className="!pt-0">
                            {membersLoading ? (
                                <p className="text-center text-slate-500 py-8">Loading events...</p>
                            ) : upcomingEvents.length === 0 ? (
                                <p className="text-center text-slate-500 py-8">No upcoming events</p>
                            ) : (
                                <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
                                    {upcomingEvents.map((ev, i) => (
                                        <div key={i} className="flex items-center gap-3 py-2">
                                            <Avatar name={ev.name} photoUrl={ev.photoUrl} size="md" />
                                            <div className="flex-1 min-w-0">
                                                <span className="text-sm text-slate-700 dark:text-slate-300 truncate block leading-tight">{ev.name}</span>
                                                <span className="text-xs text-slate-400 leading-tight">
                                                    {MONTHS[ev.month - 1].slice(0, 3)} {ev.day}
                                                </span>
                                            </div>
                                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                                                ev.type === 'birthday'
                                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                                    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                            }`}>
                                                {ev.type === 'birthday' ? 'Birthday' : 'Anniversary'}
                                            </span>
                                            <span className={`text-xs font-semibold flex-shrink-0 w-10 text-right ${
                                                ev.daysAway === 0 ? 'text-accent-500' : 'text-slate-400'
                                            }`}>
                                                {ev.daysAway === 0 ? 'Today' : ev.daysAway === 1 ? '1d' : `${ev.daysAway}d`}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="text-right mt-2">
                                <Link to="/calendar" className="text-sm font-medium text-accent-500 hover:text-accent-600">View Calendar &rarr;</Link>
                            </div>
                        </CardBody>
                    </Card>
                </div>
            </div>
        </div>
    );
}
